import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { MemberProductInterest, CreateProductInterest, UpdateProductInterest } from '@/types/member-product-interests';

export const useProductInterests = (memberId?: string) => {
  return useQuery({
    queryKey: ['member-product-interests', memberId],
    queryFn: async () => {
      if (!memberId) return [];
      
      const { data, error } = await supabase
        .from('member_product_interests')
        .select(`
          *,
          perk:perks(*)
        `)
        .eq('member_id', memberId)
        .order('interest_level', { ascending: false });

      if (error) throw error;
      return data as MemberProductInterest[];
    },
    enabled: !!memberId,
  });
};

export const useMyProductInterests = () => {
  return useQuery({
    queryKey: ['my-product-interests'],
    queryFn: async () => {
      // First get current user's member ID
      const { data: member, error: memberError } = await supabase
        .from('club_members')
        .select('id')
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id)
        .single();

      if (memberError || !member) throw memberError || new Error('Member not found');

      const { data, error } = await supabase
        .from('member_product_interests')
        .select(`
          *,
          perk:perks(*)
        `)
        .eq('member_id', member.id)
        .order('interest_level', { ascending: false });

      if (error) throw error;
      return data as MemberProductInterest[];
    },
  });
};

export const useManageProductInterests = () => {
  const queryClient = useQueryClient();

  const addInterest = useMutation({
    mutationFn: async (interest: CreateProductInterest) => {
      const { data, error } = await supabase
        .from('member_product_interests')
        .insert(interest)
        .select(`
          *,
          perk:perks(*)
        `)
        .single();

      if (error) throw error;
      return data as MemberProductInterest;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['member-product-interests'] });
      queryClient.invalidateQueries({ queryKey: ['my-product-interests'] });
    },
  });

  const updateInterest = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateProductInterest }) => {
      const { data, error } = await supabase
        .from('member_product_interests')
        .update(updates)
        .eq('id', id)
        .select(`
          *,
          perk:perks(*)
        `)
        .single();

      if (error) throw error;
      return data as MemberProductInterest;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['member-product-interests'] });
      queryClient.invalidateQueries({ queryKey: ['my-product-interests'] });
    },
  });

  const removeInterest = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('member_product_interests')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['member-product-interests'] });
      queryClient.invalidateQueries({ queryKey: ['my-product-interests'] });
    },
  });

  return {
    addInterest,
    updateInterest,
    removeInterest,
  };
};